import { IContextualMenuItem, IOverflowSetItemProps, IOverflowSetStyles, OverflowSet, ResizeGroup } from "@fluentui/react";
import React from "react";
import { IconName } from "../../../../../styles/icons";
import { getRTLIcon } from "../../../../../styles/language";
import { MenuIconButton, MenuIconButtonProps, MenuItem } from "../../../../Buttons/MenuIconButton/MenuIconButton";
import { Icon } from "../../../../Icon/Icon";
import { Stack } from "../../../../Stacks/Stack/Stack";
import { StopPropagation } from "../../../../StopPropagation/StopPropagation";
import { menuItemsPadding } from "../../../ListItemColumns/shared/styles";
import { FinalColumnAlign, MultiColumnListItemSharedBaseProps } from "../../shared/MultiColumnListItem.types";

type ListItemFinalColumnProps = Pick<MultiColumnListItemSharedBaseProps, "disabled" | "finalColumnText" | "finalColumnActions"> & {
    /**
     * Optional menu props already passed to the list item. These are needed if mixing with overflowed actions.
     */
    readonly existingMenuProps?: MenuIconButtonProps;

    /**
     * Optional iconName, rendered at the far right of the List (LTR)
     */
    readonly iconName?: IconName;

    /**
     * Whether to align the final Column's components at the end or start of the
     * Column
     */
    readonly finalColumnAlign?: FinalColumnAlign;

    /**
     * Optionally disable the contextual/overflow menu
     */
    readonly menuDisabled?: boolean;

    /**
     * If enabled, the text of ActionButtons that are not overflowed will be collapsed,
     * rendering only the icon.
     */
    readonly collapseActionText?: boolean;
};

const computeCacheKey = (primaryControls: readonly IContextualMenuItem[]): string =>
    primaryControls.map(control => control.key).toString();

export const ListItemFinalColumn: React.FC<ListItemFinalColumnProps> = ({
    finalColumnText,
    finalColumnActions = [],
    existingMenuProps = { "aria-label": "", "data-bi-id": "" },
    finalColumnAlign = "end",
    collapseActionText = false,
    disabled = false,
    menuDisabled = false,
    ...otherProps
}) => {
    const gapBetweenFinalColumnElements = 16;
    const existingMenuItems = existingMenuProps.menuItems || [];
    const hasFinalColumnInteractions = finalColumnActions.length > 0 || existingMenuItems.length > 0;

    let computedCacheKey = "";
    const mappedFinalColumnActions: IOverflowSetItemProps[] = finalColumnActions.map((action, index) => {
        computedCacheKey += `item${index}`;
        return {
            key: `item${index}`,
            name: action.props.text,
            "aria-label": action.props["aria-label"],
            "data-bi-id": action.props["data-bi-id"],
            action,
            isCollapsible: !!action.props.iconName, // If the action doesn't have an icon, it doesn't make sense to collapse it
            // Why does each action have an onRender when there is a similar onRenderItem method below? OverflowSet uses
            // the onRenderItem. When the action is overflowed, and is rendered inside the contextual MenuIconButton,
            // this onRender is called.
            onRender: () => (
                <StopPropagation key={index}>
                    {action}
                </StopPropagation>
            )
        };
    });

    interface OverflowData {
        readonly primary: IOverflowSetItemProps[];
        readonly overflow: IOverflowSetItemProps[];
        readonly cacheKey?: string;
    }

    const finalColumnInteractions: OverflowData = {
        primary: mappedFinalColumnActions,
        overflow: [],
        cacheKey: computedCacheKey
    };

    const onReduceData = (currentData: OverflowData) => {
        if (currentData.primary.length === 0) {
            return undefined;
        }

        // Push action into overflow
        const overflow = [...currentData.primary.slice(-1), ...currentData.overflow];

        // Remove the previously mentioned overflowed item from primary
        const primary = currentData.primary.slice(0, -1);

        // Compute cache for performance improvement during resizing
        const cacheKey = computeCacheKey(primary);
        return { primary, overflow, cacheKey };
    };

    const onGrowData = (currentData: OverflowData) => {
        if (currentData.overflow.length === 0) {
            return undefined;
        }

        // Move the top action from overflow out
        const overflow = [...currentData.overflow.slice(1)];

        // And place it into primary
        const primary = [...currentData.primary, ...currentData.overflow.slice(0, 1)];

        // Compute cache for performance improvement during resizing
        const cacheKey = computeCacheKey(primary);

        return { primary, overflow, cacheKey };
    };

    const onRenderItem = (item: IOverflowSetItemProps) => (
        <StopPropagation key={item.key}>
            {disabled ? React.cloneElement(item.action, { disabled }) : item.action}
        </StopPropagation>
    );

    const onRenderOverflowButton = (overflowItems: readonly any[] | undefined) => (
        <Stack verticalAlign="center">
            <MenuIconButton
                {...existingMenuProps}
                iconName="MoreVertical"
                disabled={menuDisabled || disabled}
                menuItems={overflowItems as MenuItem[]}
                menuItemsPadding={existingMenuProps.menuItemsPadding || menuItemsPadding}
            />
        </Stack>
    );

    const onRenderData = (data: OverflowData) => {
        let primary = data.primary;
        if (collapseActionText) {
            primary = primary.map((item) => (item.isCollapsible ? {
                ...item,
                action: React.cloneElement(item.action, { ...item.action.props, text: "" })
            } : item
            ));
        }

        // When final column actions are aligned to start, the Overflow Buttons pushes everything left:
        //          Action 1 Action 2 --------------------Overflow Button
        //
        // The Overflow Button should also push left when there are no actions to show either due to a lack of them or all of them
        // overflowing. For example:
        //          FinalColumnText FinalColumnText2 -----Overflow Button
        //
        // However, when actions are aligned to the end, we want the first Action to push instead:
        //          --------------------Action 1 Action 2 Overflow Button
        const overflowStyles: IOverflowSetStyles = (finalColumnAlign === "start" || primary.length === 0) ?
            {
                overflowButton: {
                    "flex-grow": 1,
                    "justify-content": "flex-end"
                }
            } : {
                item: {
                    alignItems: "center",
                    ":first-of-type": {
                        "flex-grow": 1,
                        "justify-content": "flex-end"
                    }
                },
            };

        if (data.overflow.length === 0 && existingMenuItems.length === 0) {
            // When there are no items in the overflow menu, keep padding on the end.
            overflowStyles["root"] = {
                padding: "0 8px 0 0"
            };
        }

        return (
            <OverflowSet
                items={primary}
                overflowItems={[...existingMenuItems as IOverflowSetItemProps[], ...data.overflow]}
                onRenderItem={onRenderItem}
                onRenderOverflowButton={onRenderOverflowButton}
                styles={overflowStyles}
            />
        );
    };

    return (
        <Stack horizontal={true} grow={true}>
            {finalColumnText && finalColumnText.length > 0 && (
                <Stack horizontal={true} verticalAlign="center" childrenGap={gapBetweenFinalColumnElements}>
                    {finalColumnText.map((item, index) => !disabled && item.key ? item : React.cloneElement(item, { disabled, key: item.key || index }))}
                </Stack>
            )}
            {hasFinalColumnInteractions && (
                <Stack grow={true}>
                    <ResizeGroup
                        data={finalColumnInteractions}
                        onReduceData={onReduceData}
                        onGrowData={onGrowData}
                        onRenderData={onRenderData}
                    />
                </Stack>
            )}
            {otherProps.iconName && finalColumnInteractions.overflow.length === 0 && (
                <Stack grow={true} horizontalAlign="end" verticalAlign="center" padding="0 8px 0 0">
                    <Icon
                        size="16px"
                        iconName={getRTLIcon(otherProps.iconName)}
                        color="neutralPrimary"
                    />
                </Stack>
            )}
        </Stack>
    );
};
